/**
 * Trailers API Client
 * Strapi integration for trailers management
 */

import { getHttpClient } from '@ufc/core'
import type {
  Trailer,
  TrailersFilter,
  CreateAssetDto,
  PaginatedTrailers,
  PaginationMeta,
} from '../../domain/types'
import { DEFAULT_PAGE_SIZE } from '../../domain/types'
import {
  STRAPI_ASSET_ENDPOINTS,
  type StrapiAssetListResponse,
  type StrapiAssetResponse,
  type StrapiAssetTypeListResponse,
  strapiAssetListResponseSchema,
  strapiAssetResponseSchema,
  strapiAssetTypeListResponseSchema,
  type EntityUpdateResult,
} from '../../api/api-types'
import {
  transformStrapiAssetsToTrailers,
  transformStrapiAssetToTrailer,
  transformTrailerToStrapiCreate,
  transformTrailerToStrapiUpdate,
} from '../../api/transformers'

// ============================================================================
// Default Populate Options
// ============================================================================

// Populate options - we don't populate 'type' to avoid nested relation issues
// The 'type' relation has a 'parent' field which causes "Invalid key parent" errors when populated
// We can still filter by type.code without populating it
const DEFAULT_ASSET_POPULATE: Record<string, string> = {
  'populate[tenant]': '*',
  // Don't populate type - it has nested relations that cause errors
  // We can still filter by filters[type][code][$eq] without populating
}

// ============================================================================
// Helper: Deep clean request data to remove relation fields
// ============================================================================

function deepCleanRequestData(data: Record<string, unknown>): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {}
  
  // Only include scalar fields and metadata (no relations)
  // NOTE: 'type' is a relation field, so we handle it specially to avoid sending nested objects
  const allowedScalarFields = [
    'assetNumber', 'make', 'model', 'year', 'vin', 'serialNumber',
    'licensePlate', 'assetStatus', 'capacity', 'capacityUnit', 'description'
  ]
  
  // Handle scalar fields
  allowedScalarFields.forEach(field => {
    if (field in data && data[field] !== undefined) {
      const value = data[field]
      // Only include primitives, null, or arrays of primitives
      if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        cleaned[field] = value
      }
    }
  })
  
  // Handle 'type' field specially - it's a relation, so only accept scalar values (documentId string)
  // If it's an object (like { parent: {...} }), extract documentId or exclude it
  if ('type' in data && data.type !== undefined) {
    const typeValue = data.type
    if (typeValue === null || typeof typeValue === 'string') {
      // Accept null or string (documentId)
      cleaned.type = typeValue
    } else if (typeof typeValue === 'object' && 'documentId' in typeValue) {
      // Extract documentId from relation object
      cleaned.type = (typeValue as { documentId: string }).documentId
    } else {
      // Exclude if it's an object without documentId (like { parent: {...} })
      console.warn('[TrailersAPI] Warning: type field is an object without documentId, excluding it:', typeValue)
    }
  }
  
  // Handle metadata separately - clean it to remove any nested objects that might be relations
  if (data.metadata && typeof data.metadata === 'object' && !Array.isArray(data.metadata)) {
    const metadata = data.metadata as Record<string, unknown>
    const cleanMetadata: Record<string, unknown> = {}
    
    Object.keys(metadata).forEach(key => {
      const value = metadata[key]
      // CRITICAL: Never allow 'type' in metadata if it's an object - it might be a relation
      if (key === 'type') {
        // Only allow type as a string in metadata, never as an object
        if (typeof value === 'string') {
          cleanMetadata[key] = value
        } else {
          console.warn('[TrailersAPI] Excluding type object from metadata:', value)
          // Exclude it - don't add to cleanMetadata
        }
        return
      }
      
      // Only include primitives or simple objects (like capacity: {value, unit})
      if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        cleanMetadata[key] = value
      } else if (key === 'capacity' && typeof value === 'object' && 'value' in value && 'unit' in value) {
        // Allow capacity object - but clean it to ensure no nested relations
        const capacityObj = value as Record<string, unknown>
        cleanMetadata[key] = {
          value: capacityObj.value,
          unit: capacityObj.unit,
        }
      }
      // Exclude any other objects (they might be relations like assetType)
    })
    
    if (Object.keys(cleanMetadata).length > 0) {
      cleaned.metadata = cleanMetadata
    }
  }
  
  // Don't include tenant - backend will handle it from auth context
  // CRITICAL: Explicitly exclude ALL relation fields - check both top level and nested
  const relationFields = ['assetType', 'location', 'assignedDriver', 'insurances', 'permits', 'registrations', 'tenant']
  relationFields.forEach(field => {
    delete cleaned[field]
    // Also check if it exists in the original data and log a warning
    if (field in data && data[field] !== undefined) {
      console.warn(`[TrailersAPI] Warning: Relation field '${field}' was included in request data and has been removed`)
    }
  })
  
  return cleaned
}

// ============================================================================
// Trailers API
// ============================================================================

export const trailersApi = {
  /**
   * Get all trailers (assets with type='trailer')
   */
  async getTrailers(filter?: TrailersFilter): Promise<Trailer[]> {
    const http = getHttpClient()
    const params = new URLSearchParams()

    // Filter by asset type relation (trailer)
    // Since type is now a relation, filter by the code field of the relation
    params.set('filters[type][code][$eq]', 'trailer')

    // Add status filter if provided
    if (filter?.status && filter.status !== 'all') {
      const statusMap: Record<string, string> = {
        available: 'active',
        assigned: 'active',
        maintenance: 'maintenance',
        'out-of-service': 'inactive',
      }
      params.set('filters[assetStatus][$eq]', statusMap[filter.status] || 'active')
    }

    // Add search filter if provided
    if (filter?.search) {
      params.set('filters[$or][0][assetNumber][$containsi]', filter.search)
      params.set('filters[$or][1][licensePlate][$containsi]', filter.search)
      params.set('filters[$or][2][make][$containsi]', filter.search)
      params.set('filters[$or][3][model][$containsi]', filter.search)
    }

    // Add populate params
    Object.entries(DEFAULT_ASSET_POPULATE).forEach(([key, value]) => {
      params.set(key, value)
    })

    const url = `${STRAPI_ASSET_ENDPOINTS.LIST}?${params.toString()}`
    const response = await http.get<StrapiAssetListResponse>(url)
    
    // Handle response validation more gracefully
    try {
      const validated = strapiAssetListResponseSchema.parse(response.data)
      return transformStrapiAssetsToTrailers(validated.data)
    } catch (error) {
      console.error('Strapi response validation error:', error)
      console.error('Response data:', response.data)
      // Try to transform anyway if we have data
      if (response.data?.data && Array.isArray(response.data.data)) {
        return transformStrapiAssetsToTrailers(response.data.data)
      }
      throw error
    }
  },

  /**
   * Get trailers with pagination
   */
  async getTrailersPaginated(filter?: TrailersFilter): Promise<PaginatedTrailers> {
    const http = getHttpClient()
    const params = new URLSearchParams()

    // Pagination
    const page = filter?.page ?? 1
    const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE
    params.set('pagination[page]', String(page))
    params.set('pagination[pageSize]', String(pageSize))

    // Filter by asset type relation (trailer)
    params.set('filters[type][code][$eq]', 'trailer')

    // Add status filter if provided
    if (filter?.status && filter.status !== 'all') {
      const statusMap: Record<string, string> = {
        available: 'active',
        assigned: 'active',
        maintenance: 'maintenance',
        'out-of-service': 'inactive',
      }
      params.set('filters[assetStatus][$eq]', statusMap[filter.status] || 'active')
    }

    // Add type filter if provided (trailer subtype like flatbed, tanker, etc.)
    if (filter?.type && filter.type !== 'all') {
      params.set('filters[metadata][type][$eq]', filter.type)
    }

    // Add search filter if provided
    if (filter?.search) {
      params.set('filters[$or][0][assetNumber][$containsi]', filter.search)
      params.set('filters[$or][1][licensePlate][$containsi]', filter.search)
      params.set('filters[$or][2][make][$containsi]', filter.search)
      params.set('filters[$or][3][model][$containsi]', filter.search)
    }

    // Add populate params
    Object.entries(DEFAULT_ASSET_POPULATE).forEach(([key, value]) => {
      params.set(key, value)
    })

    const url = `${STRAPI_ASSET_ENDPOINTS.LIST}?${params.toString()}`
    const response = await http.get<StrapiAssetListResponse>(url)

    // Extract pagination from response
    const meta = (response.data as any)?.meta?.pagination
    const pagination: PaginationMeta = {
      page: meta?.page ?? page,
      pageSize: meta?.pageSize ?? pageSize,
      pageCount: meta?.pageCount ?? 1,
      total: meta?.total ?? 0,
    }

    // Handle response validation
    try {
      const validated = strapiAssetListResponseSchema.parse(response.data)
      return {
        data: transformStrapiAssetsToTrailers(validated.data),
        pagination,
      }
    } catch (error) {
      console.error('Strapi response validation error:', error)
      if (response.data?.data && Array.isArray(response.data.data)) {
        return {
          data: transformStrapiAssetsToTrailers(response.data.data),
          pagination,
        }
      }
      throw error
    }
  },

  /**
   * Get a single trailer by documentId
   */
  async getTrailer(documentId: string): Promise<Trailer | null> {
    const http = getHttpClient()
    const params = new URLSearchParams()
    Object.entries(DEFAULT_ASSET_POPULATE).forEach(([key, value]) => {
      params.set(key, value)
    })

    const url = `${STRAPI_ASSET_ENDPOINTS.DETAIL(documentId)}?${params.toString()}`
    const response = await http.get<StrapiAssetResponse>(url)
    
    // Handle response validation more gracefully
    try {
      const validated = strapiAssetResponseSchema.parse(response.data)
      return transformStrapiAssetToTrailer(validated.data)
    } catch (error) {
      console.error('Strapi response validation error:', error)
      console.error('Response data:', response.data)
      // Try to transform anyway if we have data
      if (response.data?.data) {
        return transformStrapiAssetToTrailer(response.data.data)
      }
      return null
    }
  },

  /**
   * Create a new trailer
   */
  async createTrailer(trailer: Partial<Trailer> & { tenantId: string }): Promise<Trailer> {
    const http = getHttpClient()
    
    // Fetch the asset type documentId for 'trailer' code - REQUIRED
    // This page is only for trailers, so we must always have the trailer type documentId
    let typeDocumentId: string | undefined
    try {
      const assetTypes = await this.getAssetTypes()
      const trailerType = assetTypes.find(at => at.code === 'trailer')
      if (trailerType) {
        typeDocumentId = trailerType.documentId
      } else {
        throw new Error('Trailer asset type not found in Strapi. Please ensure the asset type with code "trailer" exists.')
      }
    } catch (error) {
      console.error('Failed to fetch asset type for trailer:', error)
      throw new Error(`Cannot create trailer: Failed to fetch trailer asset type. ${error instanceof Error ? error.message : String(error)}`)
    }
    
    if (!typeDocumentId) {
      throw new Error('Cannot create trailer: Trailer asset type documentId is missing.')
    }
    
    const requestData = transformTrailerToStrapiCreate(trailer, typeDocumentId)
    
    // Log the request data before cleaning to debug
    console.log('[TrailersAPI] Request data before cleaning:', JSON.stringify(requestData, null, 2))
    
    // Deep clean the data object to remove any relation fields with nested data
    if (requestData.data && typeof requestData.data === 'object') {
      const cleaned = deepCleanRequestData(requestData.data as Record<string, unknown>)
      
      // Log after first cleaning pass
      console.log('[TrailersAPI] Request data after deepCleanRequestData:', JSON.stringify(cleaned, null, 2))
      
      // Final safety: JSON serialize/parse to remove any hidden nested objects
      try {
        const serialized = JSON.stringify(cleaned)
        const parsed = JSON.parse(serialized) as Record<string, unknown>
        
        // One final pass - remove any relation fields that might have survived
        const finalClean: Record<string, unknown> = {}
        const allowedFields = ['assetNumber', 'type', 'make', 'model', 'year', 'vin', 'serialNumber', 'licensePlate', 'assetStatus', 'capacity', 'capacityUnit', 'description', 'metadata']
        
        Object.keys(parsed).forEach(key => {
          if (allowedFields.includes(key)) {
            const value = parsed[key]
            // Special handling for 'type' - ensure it's only a scalar (documentId string or null)
            if (key === 'type') {
              if (value === null || typeof value === 'string') {
                finalClean[key] = value
              } else if (typeof value === 'object' && value !== null && 'documentId' in value) {
                // Extract documentId if it's an object
                finalClean[key] = (value as { documentId: string }).documentId
              }
              // Otherwise exclude it (it's a nested relation object)
            } else {
              finalClean[key] = value
            }
          }
        })
        
        requestData.data = finalClean
      } catch (e) {
        // If serialization fails, use cleaned data as-is but still remove tenant and relations
        delete cleaned.tenant
        delete cleaned.assetType
        requestData.data = cleaned
      }
      
      // Absolute final check - ensure tenant and relation fields are definitely not there
      const finalData = requestData.data as Record<string, unknown>
      delete finalData.tenant
      delete finalData.assetType
      delete finalData.location
      delete finalData.assignedDriver
      
      // CRITICAL: Ensure 'type' is only a scalar (documentId string or null), not an object
      if ('type' in finalData) {
        const typeValue = finalData.type
        if (typeValue === null || typeof typeValue === 'string') {
          // Good - it's already a scalar
        } else if (typeof typeValue === 'object' && typeValue !== null) {
          // It's an object - extract documentId or remove it
          if ('documentId' in typeValue) {
            finalData.type = (typeValue as { documentId: string }).documentId
            console.log('[TrailersAPI] Extracted documentId from type object:', finalData.type)
          } else {
            // If it's an object without documentId (like { parent: {...} }), remove it
            console.warn('[TrailersAPI] Removing invalid type object (has nested properties):', JSON.stringify(typeValue))
            delete finalData.type
          }
        }
      }
      
      // One more pass - recursively check metadata for any nested type objects
      if (finalData.metadata && typeof finalData.metadata === 'object' && finalData.metadata !== null) {
        const metadata = finalData.metadata as Record<string, unknown>
        if ('type' in metadata && typeof metadata.type === 'object' && metadata.type !== null) {
          console.warn('[TrailersAPI] Found type object in metadata, removing it:', metadata.type)
          delete metadata.type
        }
      }
      
      // Log final request data before sending
      console.log('[TrailersAPI] Final request data to send:', JSON.stringify(finalData, null, 2))
    }
    
    // ABSOLUTE FINAL CHECK: Right before sending, ensure type is scalar
    if (requestData.data && typeof requestData.data === 'object') {
      const dataToSend = requestData.data as Record<string, unknown>
      if ('type' in dataToSend) {
        const typeVal = dataToSend.type
        if (typeof typeVal === 'object' && typeVal !== null) {
          console.error('[TrailersAPI] CRITICAL: type is still an object right before sending!', JSON.stringify(typeVal))
          if ('documentId' in typeVal) {
            dataToSend.type = (typeVal as { documentId: string }).documentId
          } else {
            delete dataToSend.type
          }
        }
      }
      
      // Also check metadata one more time
      if (dataToSend.metadata && typeof dataToSend.metadata === 'object' && dataToSend.metadata !== null) {
        const meta = dataToSend.metadata as Record<string, unknown>
        if ('type' in meta && typeof meta.type === 'object' && meta.type !== null) {
          console.error('[TrailersAPI] CRITICAL: type object found in metadata right before sending!', JSON.stringify(meta.type))
          delete meta.type
        }
      }
      
      console.log('[TrailersAPI] Request data RIGHT BEFORE HTTP POST:', JSON.stringify(dataToSend, null, 2))
    }
    
    const response = await http.post<StrapiAssetResponse>(
      STRAPI_ASSET_ENDPOINTS.CREATE,
      requestData
    )
    
    // Handle response validation more gracefully
    try {
      const validated = strapiAssetResponseSchema.parse(response.data)
      return transformStrapiAssetToTrailer(validated.data)
    } catch (error) {
      console.error('Strapi response validation error:', error)
      console.error('Response data:', response.data)
      // Try to transform anyway if we have data
      if (response.data?.data) {
        return transformStrapiAssetToTrailer(response.data.data)
      }
      throw error
    }
  },

  /**
   * Update an existing trailer
   */
  async updateTrailer(documentId: string, trailer: Partial<Trailer>): Promise<EntityUpdateResult<Trailer>> {
    const http = getHttpClient()
    const requestData = transformTrailerToStrapiUpdate(trailer)
    
    // Deep clean the data object to remove any relation fields with nested data
    if (requestData.data && typeof requestData.data === 'object') {
      const cleaned = deepCleanRequestData(requestData.data as Record<string, unknown>)
      
      // For updates, also remove fields that shouldn't be updated
      delete cleaned.assetNumber
      delete cleaned.type
      delete cleaned.tenant
      
      // Final safety: JSON serialize/parse to remove any hidden nested objects
      try {
        const serialized = JSON.stringify(cleaned)
        requestData.data = JSON.parse(serialized) as Record<string, unknown>
      } catch (e) {
        // If serialization fails, use cleaned data as-is
        requestData.data = cleaned
      }
      
      // One more explicit check - ensure assetType and all relations are definitely not there
      const finalData = requestData.data as Record<string, unknown>
      delete finalData.assetType
      delete finalData.location
      delete finalData.assignedDriver
      delete finalData.insurances
      delete finalData.permits
      delete finalData.registrations
    }
    
    const response = await http.put<StrapiAssetResponse>(
      STRAPI_ASSET_ENDPOINTS.UPDATE(documentId),
      requestData
    )

    const rawData: any = response?.data
    const deactivation = {
      affectedOrders: (rawData?.affectedOrders as string[]) ?? [],
      affectedResources: (rawData?.affectedResources as string[]) ?? [],
    }

    // Handle response validation more gracefully
    let entity: Trailer
    try {
      if (!response || !response.data) {
        throw new Error('Empty response from server')
      }

      if (rawData && 'id' in rawData && 'documentId' in rawData) {
        entity = transformStrapiAssetToTrailer(rawData as any)
      } else {
        const validated = strapiAssetResponseSchema.parse(rawData)
        entity = transformStrapiAssetToTrailer(validated.data)
      }
    } catch (error) {
      console.error('Strapi update response validation error:', error)
      console.error('Response:', response)
      console.error('Response data:', response?.data)

      if (response?.data) {
        if ('id' in response.data && 'documentId' in response.data) {
          entity = transformStrapiAssetToTrailer(response.data as any)
        } else if ((response.data as any).data && 'id' in (response.data as any).data) {
          entity = transformStrapiAssetToTrailer((response.data as any).data)
        } else {
          const errorMessage = error instanceof Error ? error.message : String(error)
          throw new Error(`Failed to update trailer: ${errorMessage}`)
        }
      } else {
        const errorMessage = error instanceof Error ? error.message : String(error)
        throw new Error(`Failed to update trailer: ${errorMessage}`)
      }
    }

    return { entity, deactivation }
  },

  /**
   * Get asset types (for trailer type selection)
   */
  async getAssetTypes(): Promise<Array<{ id: string; documentId: string; code: string; name: string }>> {
    const http = getHttpClient()
    const params = new URLSearchParams()
    params.set('filters[code][$eq]', 'trailer')
    
    const url = `${STRAPI_ASSET_ENDPOINTS.ASSET_TYPES}?${params.toString()}`
    const response = await http.get<StrapiAssetTypeListResponse>(url)
    const validated = strapiAssetTypeListResponseSchema.parse(response.data)
    return validated.data.map((dto) => ({
      id: String(dto.id),
      documentId: dto.documentId,
      code: dto.code,
      name: dto.name,
    }))
  },

  /**
   * Delete a trailer
   */
  async deleteTrailer(documentId: string): Promise<void> {
    const http = getHttpClient()
    await http.delete(STRAPI_ASSET_ENDPOINTS.UPDATE(documentId))
  },
}
